import React from 'react';
import ReactDOM from 'react-dom/client';
import { NextUIProvider } from '@nextui-org/react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import Clarity from '@microsoft/clarity';

import i18n from './utils/i18n.js';
import { CLARITY_PROJECT_ID, hasConsent, startGoogleWithConsent } from './utils/cookieConsent.js';
import { startMetaPixel } from './utils/metaPixel.js';
import { captureAcquisition } from './utils/acquisition.js';
import { installAppZoomLock } from './utils/appZoom.js';
import { installFocusRing } from './utils/focusRing.js';
import { captureInstallPrompt } from './utils/installPrompt.js';
import { isAppOnPhone, isCrawler } from './utils/pwa.js';
import { installServiceWorker } from './utils/serviceWorker.js';
import { installViewportShift, viewportStuckMessage } from './utils/viewportShift.js';
import { freshnessLink, installReturnRefresh } from './utils/quietRefresh.js';

import App from './App.jsx';
import './index.css';
import './styles/redesign.css';
import './styles/pwa.css';
import './styles/viewport-shift.css';

// The browser's install prompt can fire before React mounts (docs/pwa.md)
captureInstallPrompt();

if (!isCrawler(navigator.userAgent)) {
  installServiceWorker();
}

// The installed app on a phone: what only the CSS needs to know, set before the first paint
document.documentElement.classList.toggle('mt-app', isAppOnPhone());
// The installed app doesn't zoom on a touch screen, from the first paint
installAppZoomLock();
// The focus ring shows after a key, never after a tap or a click (utils/focusRing.js)
installFocusRing();

// Sync HTML lang attribute with i18n language
document.documentElement.lang = i18n.language || 'pt';

// Only init Clarity if user has given cookie consent
if (hasConsent()) {
  Clarity.init(CLARITY_PROJECT_ID);
}

// A visitor who already accepted: load Google Analytics, AdSense and the Meta Pixel (nothing from them otherwise)
if (hasConsent()) {
  startGoogleWithConsent();
  startMetaPixel();
}

// The visit's campaign tags, sent with a signup (utils/acquisition.js)
captureAcquisition();

Sentry.init({
  dsn: 'https://baa135d3a6cbf51cee724f1023044e53@o4506814380769280.ingest.sentry.io/4506814382342144',
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
  tracePropagationTargets: [
    /^https:\/\/meutreino\.fit/,
    /^https:\/\/myworkoutskill\.com/,
  ],
});

// iOS can keep a keyboard's height with no keyboard up: the fixed and sticky chrome follows the offset (docs/pwa.md)
installViewportShift((info) => Sentry.captureMessage(viewportStuckMessage(info), 'warning'));

const { VITE_GRAPHQL_URL } = import.meta.env;

const httpLink = createHttpLink({
  uri: VITE_GRAPHQL_URL,
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const authToken = localStorage.getItem('authToken');
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: authToken ? `bearer ${authToken}` : '',
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  const { pathname, search } = window.location;

  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions }) => {
      if (
        !/^\/login/.test(pathname) &&
        extensions &&
        ['UNAUTHENTICATED', 'FORBIDDEN'].includes(extensions.code)
      ) {
        client.resetStore();
        localStorage.removeItem('authToken');
        window.location = `/login${pathname}${search}`;
      }
    });
  }

  if (networkError) {
    const { statusCode } = networkError;

    if (!/^\/login/.test(pathname) && [401].includes(statusCode)) {
      client.resetStore();
      localStorage.removeItem('authToken');
      window.location = `/login${pathname}${search}`;
    }
  }
});

const client = new ApolloClient({
  link: freshnessLink.concat(errorLink.concat(authLink.concat(httpLink))),
  cache: new InMemoryCache(),
});
installReturnRefresh(client);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <NextUIProvider>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:bg-[#067f50] focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-semibold"
      >
        {i18n.language === 'pt' ? 'Pular para o conteúdo' : 'Skip to content'}
      </a>
      <main id="main-content" tabIndex="-1" className="dark text-foreground bg-background outline-none">
        <ApolloProvider client={client}>
          <App />
        </ApolloProvider>
      </main>
    </NextUIProvider>
  </React.StrictMode>
);
